import { useQuery } from '@tanstack/react-query';
import type { ActivityLogDto, GetAllParamsDto } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { getUserDataForResults } from './useFim3Api';

interface GetActivityLogsParams extends GetAllParamsDto {
  productAreaId?: number;
}

export interface ActivityLogWithUserDto extends ActivityLogDto {
  displayName?: string;
  creatorName?: string;
  entityName?: string | null;
}

interface GetActivityLogsResponse {
  data: ActivityLogWithUserDto[];
  pagination: {
    offset: number;
    limit: number;
  };
}

export const useActivityLogsApi = (httpClient: AxiosInstance) => {
  const baseUrl = '/activity-logs';

  const getActivityLogs = async (
    params: GetActivityLogsParams,
  ): Promise<GetActivityLogsResponse> => {
    const { data } = await httpClient.get<{
      data: ActivityLogDto[];
      pagination: { offset: number; limit: number };
    }>(baseUrl, { params });

    const userIds = data.data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data as Array<{
        id: number;
        displayName?: string;
        creatorName?: string;
        email?: string;
      }>,
      (item) => (item as ActivityLogDto).userId,
    );

    return {
      ...data,
      data: result as ActivityLogWithUserDto[],
    };
  };

  const useGetActivityLogs = (params: GetActivityLogsParams, enabled = true) => {
    return useQuery({
      queryKey: [baseUrl, params],
      queryFn: () => getActivityLogs(params),
      enabled,
    });
  };

  return {
    getActivityLogs,
    useGetActivityLogs,
  };
};
